/**
 * Public pre-sign waiver page.
 *
 * The booking confirmation email contains a direct link to this page with a
 * `request_id` query param. No login is required — visitors who lost the
 * email can also reach this page via their dashboard, but the canonical
 * entry point is the email link. The actual signature still flows through
 * the existing public POST /assessment-requests/{id}/sign-waiver endpoint
 * which records the IP, user-agent, and frozen waiver text.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2, ArrowLeft } from "lucide-react";
import { WAIVER_BOOKING_TEXT, WAIVER_BOOKING_VERSION } from "@/lib/waiver";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waiver() {
    const [params] = useSearchParams();
    const requestId = params.get("request_id");

    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const [signedName, setSignedName] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [readToBottom, setReadToBottom] = useState(false);
    const [relationship, setRelationship] = useState("self");

    const scrollRef = useRef(null);

    // Detect if any athlete on the booking is a minor → unlock relationship dropdown.
    const hasMinor = useMemo(() => {
        if (!summary?.athletes?.length) return false;
        return summary.athletes.some((a) => {
            if (!a.dob) return summary.signup_for !== "self";
            const dob = new Date(a.dob);
            const age = (Date.now() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
            return age < 18;
        });
    }, [summary]);

    useEffect(() => {
        if (!requestId) {
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const r = await axios.get(`${API}/assessment-requests/${requestId}/summary`);
                setSummary(r.data);
                if (r.data.parent_name) setSignedName(r.data.parent_name);
                setRelationship(r.data.signup_for === "self" ? "self" : "parent");
                if (r.data.waiver_signed) setDone(true);
            } catch (e) {
                toast.error("Couldn't load your booking", { description: e?.response?.data?.detail });
            } finally {
                setLoading(false);
            }
        })();
    }, [requestId]);

    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
            setReadToBottom(true);
        }
    };

    const canSign = readToBottom && agreed && signedName.trim().length > 1;

    const submit = async () => {
        if (!canSign) {
            toast.error(
                !readToBottom ? "Please scroll through the full waiver first." :
                !agreed ? "Confirm you've read and agree." :
                "Type your full legal name to sign."
            );
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/assessment-requests/${requestId}/sign-waiver`, {
                signed_name: signedName.trim(),
                version: WAIVER_BOOKING_VERSION,
                relationship,
                waiver_text_snapshot: WAIVER_BOOKING_TEXT,
                agreed: true,
            });
            setDone(true);
        } catch (e) {
            toast.error("Couldn't sign waiver", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- Empty / error states ----------
    if (!requestId) {
        return (
            <Shell>
                <div className="text-center py-12 max-w-md mx-auto">
                    <h1 className="font-display text-3xl tracking-wider text-white mb-3">MISSING BOOKING</h1>
                    <p className="text-zinc-300 text-sm">
                        This page needs a valid booking link. Check the email we sent you when you booked
                        your Initial session, or just bring it up at the gym — we'll have a paper version
                        ready.
                    </p>
                    <Link to="/" data-testid="waiver-home-link" className="btn-ghost-tactical mt-6 inline-flex">
                        <ArrowLeft className="w-4 h-4" /> Back to home
                    </Link>
                </div>
            </Shell>
        );
    }
    if (loading) {
        return (
            <Shell>
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
                </div>
            </Shell>
        );
    }
    if (!summary) {
        return (
            <Shell>
                <div className="text-center py-12 max-w-md mx-auto">
                    <h1 className="font-display text-3xl tracking-wider text-white mb-3">BOOKING NOT FOUND</h1>
                    <p className="text-zinc-300 text-sm">
                        We couldn't find that booking. It may have been dismissed, or the link may be broken.
                        We'll have a paper waiver ready at the gym — no problem.
                    </p>
                    <Link to="/" data-testid="waiver-home-link" className="btn-ghost-tactical mt-6 inline-flex">
                        <ArrowLeft className="w-4 h-4" /> Back to home
                    </Link>
                </div>
            </Shell>
        );
    }

    if (done) {
        return (
            <Shell>
                <div data-testid="waiver-success" className="text-center py-12 max-w-md mx-auto">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-navy-900/60 ring-1 ring-brand-navy-700 mb-5">
                        <CheckCircle2 className="w-8 h-8 text-brand-navy-300" />
                    </div>
                    <h1 className="font-display text-3xl tracking-wider text-white mb-2">YOU'RE ALL SET.</h1>
                    <p className="text-zinc-300 text-sm leading-relaxed">
                        Waiver signed — no paperwork on day one. Just show up in workout clothes
                        with a good attitude and we'll handle the rest.
                    </p>
                    <Link to="/" data-testid="waiver-home-link" className="btn-tactical mt-7 inline-flex">Back to home</Link>
                </div>
            </Shell>
        );
    }

    // ---------- Sign view ----------
    return (
        <Shell>
            <div data-testid="waiver-page" className="max-w-2xl mx-auto">
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand-navy-300 mb-3 inline-flex items-center gap-2">
                    <span className="inline-block w-8 h-px bg-brand-navy-600" />
                    Pre-sign your waiver
                </div>
                <h1 className="font-display text-4xl md:text-5xl tracking-wider text-white leading-[0.95]">
                    SAVE TIME ON DAY ONE.
                </h1>
                <p className="text-zinc-300 mt-3 leading-relaxed">
                    Hi <span className="text-white font-semibold">{summary.parent_name}</span> — sign your
                    standard participation waiver below and skip the clipboard at your Initial session.
                    Same form every athletic facility uses; takes about 30 seconds.
                </p>

                {summary.athletes?.length > 0 && (
                    <div className="mt-5 border border-zinc-800 rounded-lg p-3 text-xs text-zinc-300">
                        <span className="text-zinc-400">Booking for:</span>{" "}
                        <span className="text-zinc-100">
                            {summary.athletes.map((a) => a.name).join(", ")}
                        </span>
                    </div>
                )}

                <div className="mt-7 border border-brand-navy-900 bg-brand-navy-950/40 rounded-lg p-4 flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-brand-navy-300 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-zinc-200 leading-relaxed">
                        <strong className="text-white">No pressure.</strong> If you'd rather sign in person at
                        the gym, just close this tab — coach has paper copies ready. Otherwise, scroll through
                        the agreement, agree, and type your name.
                    </div>
                </div>

                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    data-testid="waiver-scroll"
                    className="mt-6 max-h-[420px] overflow-y-auto bg-black border border-zinc-900 rounded-lg p-5 text-[12px] text-zinc-200 leading-relaxed whitespace-pre-wrap font-mono"
                >
                    {WAIVER_BOOKING_TEXT}
                </div>
                <div className="text-[11px] text-zinc-300 mt-2 flex items-center gap-2">
                    {readToBottom ? (
                        <span className="text-brand-navy-300 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> You've reviewed the full waiver
                        </span>
                    ) : (
                        <span>Scroll to the end of the waiver to continue</span>
                    )}
                </div>

                {hasMinor && (
                    <label className="block mt-6">
                        <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-300">Signing as</span>
                        <select
                            value={relationship}
                            onChange={(e) => setRelationship(e.target.value)}
                            data-testid="waiver-relationship"
                            className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                        >
                            <option value="parent">Parent</option>
                            <option value="guardian">Legal Guardian</option>
                        </select>
                    </label>
                )}

                <label className="flex items-start gap-2 text-sm text-zinc-200 mt-6">
                    <input
                        type="checkbox"
                        data-testid="waiver-agree"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        disabled={!readToBottom}
                        className="mt-1 disabled:opacity-40"
                    />
                    <span>
                        I have read and agree to the Maximum Sports Conditioning Participant Agreement above
                        {hasMinor && " — I am the named athlete's parent or legal guardian and am authorized to sign on their behalf"}.
                    </span>
                </label>

                <label className="block mt-5">
                    <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-300">Type your full legal name to sign *</span>
                    <input
                        type="text"
                        data-testid="waiver-signed-name"
                        value={signedName}
                        onChange={(e) => setSignedName(e.target.value)}
                        placeholder="Your full legal name"
                        className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                    />
                </label>

                <p className="text-[11px] text-zinc-400 mt-3">
                    Waiver version <span className="text-zinc-200">{WAIVER_BOOKING_VERSION}</span> · Signed {new Date().toLocaleDateString()} · Photo / video release will be collected separately at member onboarding.
                </p>

                <div className="flex justify-end gap-2 mt-7">
                    <Link to="/" data-testid="waiver-cancel" className="btn-ghost-tactical">I'll sign at the gym</Link>
                    <button
                        type="button"
                        data-testid="waiver-submit"
                        onClick={submit}
                        disabled={submitting || !canSign}
                        className="btn-tactical disabled:opacity-50"
                    >
                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        Sign &amp; save
                    </button>
                </div>
            </div>
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="min-h-screen bg-brand-obsidian text-white pt-28 pb-16 px-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(31,45,85,0.45),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(230,0,0,0.10),transparent_55%)] pointer-events-none" />
            <div className="relative">{children}</div>
        </div>
    );
}
